import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

export type AccessTokenState = 'valid' | 'expired' | 'unknown' | 'missing';

type CookieNameConfig = {
  accessTokenNames: string[];
  refreshTokenNames: string[];
};

const DEFAULT_COOKIE_NAMES: CookieNameConfig = {
  // Keep this list small and easy to adjust. Prefer exact backend cookie names.
  accessTokenNames: [
    'Access-Token',
    'access_token',
    'accessToken',
    'pc_access_token',
    'pc-access-token',
  ],
  refreshTokenNames: [
    'Refresh-Token',
    'refresh_token',
    'refreshToken',
    'pc_refresh_token',
    'pc-refresh-token',
  ],
};

function readLocalOverride(): Partial<CookieNameConfig> {
  try {
    const raw = window.localStorage.getItem('pc.auth.cookieNames');
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Partial<CookieNameConfig>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function base64UrlToJson(payloadB64Url: string): unknown {
  const b64 = payloadB64Url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (b64.length % 4)) % 4;
  const padded = `${b64}${'='.repeat(padLen)}`;
  const json = atob(padded);
  return JSON.parse(json);
}

function tryGetJwtExpMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const payload = base64UrlToJson(parts[1]);
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null;
    const rec = payload as Record<string, unknown>;
    const exp = rec['exp'];
    if (typeof exp !== 'number' || !Number.isFinite(exp)) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthCookieStore {
  private readonly cookies = inject(CookieService);

  private readonly names: CookieNameConfig = {
    ...DEFAULT_COOKIE_NAMES,
    ...readLocalOverride(),
  };

  hasRefreshTokenCookie(): boolean {
    return this.names.refreshTokenNames.some((n) => this.cookies.check(n));
  }

  getAllCookies(): Record<string, string> {
    return this.names.accessTokenNames.reduce(
      (acc, n) => {
        const v = this.cookies.get(n);
        if (!v || !v.trim()) return acc;
        acc[n] = v.trim();
        return acc;
      },
      {} as Record<string, string>,
    );
  }

  getAccessToken(): string | null {
    for (const n of this.names.accessTokenNames) {
      const v = this.cookies.get(n);
      if (v && v.trim()) return v.trim();
    }
    return null;
  }

  getAccessTokenState(nowMs = Date.now(), leewayMs = 30_000): AccessTokenState {
    const token = this.getAccessToken();
    if (!token) return 'missing';

    // Non-JWT / HttpOnly cookies will be unreadable or not parseable; treat as unknown.
    const expMs = tryGetJwtExpMs(token);
    if (!expMs) return 'unknown';

    return expMs <= nowMs + leewayMs ? 'expired' : 'valid';
  }
}
